import {
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Link,
  useSearchParams
} from "react-router-dom";
import Config from "../utils/Config";
import {
  fetchFromAPI,
  formatDateTime,
  normalizeUrl
} from "../utils/functions";
import { buildUrl } from "../utils/buildUrl";
import useTitle from "../hooks/useTitle";
import "./BlogList.css";

const imgSrc = (post) => {
  try {
    if (post?.coverImagePath) return buildUrl(post.coverImagePath);
  } catch { }
  return Config.CONST.PLACEHOLDER;
};
const onImgError = (e) => {
  if (e.currentTarget.dataset.fallbackApplied) return;
  e.currentTarget.dataset.fallbackApplied = "1";
  e.currentTarget.src = Config.CONST.PLACEHOLDER;
};

function stripHtml(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

const LIMIT_OPTIONS = [6, 9, 12, 18];
const DEFAULT_LIMIT = 9;

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [sp, setSp] = useSearchParams();
  const dealer = sp.get("dealer") || "";
  const site = sp.get("site") || "";

  // derive paging from URL (with sane fallbacks)
  const page = Math.max(1, parseInt(sp.get("page") || "1", 10));
  const limitRaw = parseInt(sp.get("limit") || `${DEFAULT_LIMIT}`, 10);
  const limit = LIMIT_OPTIONS.includes(limitRaw) ? limitRaw : DEFAULT_LIMIT;

  // forward dealer/site to post links
  const baseQs = useMemo(() => {
    const q = new URLSearchParams();
    if (dealer) q.set("dealer", dealer);
    if (site) q.set("site", site);
    return q.toString();
  }, [dealer, site]);

  useTitle("News & Guides");

  // helper to update URL params (keeps dealer/site)
  const updateParams = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSp(next, { replace: true });
    if (Object.prototype.hasOwnProperty.call(patch, "page")) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    let ignore = false;
    setLoading(true);

    const params = new URLSearchParams();
    if (dealer) params.set("dealer", dealer);
    params.set("page", String(page));
    params.set("limit", String(limit));

    (async () => {
      try {
        const { data } = await fetchFromAPI(
          `${Config.ENDPOINTS.BLOG_LIST}?${params.toString()}`,
          { method: "GET" }
        );

        if (ignore) return;

        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];

        setPosts(items);
        setTotal(typeof data?.total === "number" ? data.total : items.length);
        setErr(items.length ? "" : "No posts yet.");
      } catch {
        if (!ignore) setErr("We couldn’t load posts right now.");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [dealer, page, limit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const firstIdx = total ? (page - 1) * limit + 1 : 0;
  const lastIdx = Math.min(total, page * limit);

  // compact page list (memoized)
  const pageList = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const s = new Set(
      [1, 2, totalPages - 1, totalPages, page - 1, page, page + 1].filter(
        (n) => n >= 1 && n <= totalPages
      )
    );
    const arr = Array.from(s).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      out.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] !== arr[i] + 1) out.push(0); // 0 = gap
    }
    return out;
  }, [page, totalPages]);

  return (
    <div className="blog-container">
      <div className="login-box legal-container">
        <section className="blog-list">
          <div className="post-crumbs">
            <a
              className="crumb-back"
              href={normalizeUrl(site)}   // site might be "guns.ffls.io:5173" or already have https
              aria-label="Back to all posts"
            >
              Back to website
            </a>
          </div>
          <header className="blog-list__head blog-list__head--with-tools">
            <div className="bl-title">
              <h1>All posts</h1>
              {!!total && (
                <p className="blog-list__count">
                  ({total} {total === 1 ? "post" : "posts"})
                </p>
              )}
            </div>

            <div className="bl-tools">
              <label className="bl-limit">
                <span>Items per page</span>
                <select
                  value={limit}
                  onChange={(e) =>
                    updateParams({ page: 1, limit: parseInt(e.target.value, 10) })
                  }
                  aria-label="Items per page"
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </header>

          {loading ? (
            <div className="blog-cards">
              {Array.from({ length: limit }).map((_, i) => (
                <article className="post-card is-skeleton" key={i}>
                  <div className="pc-media" />
                  <div className="pc-body">
                    <div className="pc-title" />
                    <div className="pc-excerpt" />
                    <div className="pc-excerpt short" />
                  </div>
                </article>
              ))}
            </div>
          ) : err ? (
            <div className="blog-empty">{err}</div>
          ) : (
            <>
              <div className="blog-cards">
                {posts.map((p) => {
                  const href = `/blog/${p.slug}${baseQs ? `?${baseQs}` : ""}`;
                  const excerpt =
                    (p.excerpt && p.excerpt.trim()) ||
                    stripHtml(p.content || "").slice(0, 160) +
                    (p.content ? "…" : "");

                  return (
                    <article className="post-card" key={p._id}>
                      <Link className="pc-media" to={href} aria-label={p.title}>
                        <img
                          src={imgSrc(p)}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          onError={onImgError}
                        />
                      </Link>

                      <div className="pc-body">
                        <time className="pc-date">
                          {formatDateTime(p.publishedAt || p.createdAt)}
                        </time>
                        <h3 className="pc-title">
                          <Link to={href}>{p.title}</Link>
                        </h3>
                        {excerpt && <p className="pc-excerpt">{excerpt}</p>}
                        <div className="pc-actions">
                          <Link className="pc-read" to={href}>
                            Read →
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Pagination */}
              <div className="blog-pager" role="navigation" aria-label="Pagination">
                <div className="range">
                  {total ? (
                    <>
                      Showing <strong>{firstIdx}</strong>–<strong>{lastIdx}</strong>{" "}
                      of <strong>{total}</strong>
                    </>
                  ) : null}
                </div>

                <div className="pages">
                  <button
                    className="btn-nav"
                    disabled={page <= 1}
                    onClick={() => updateParams({ page: page - 1 })}
                    aria-label="Previous page"
                  >
                    ← Prev
                  </button>

                  {pageList.map((n, i) =>
                    n === 0 ? (
                      <span key={`gap-${i}`} className="gap" aria-hidden="true">
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        className={`btn-page ${n === page ? "is-active" : ""}`}
                        onClick={() => updateParams({ page: n })}
                        aria-current={n === page ? "page" : undefined}
                      >
                        {n}
                      </button>
                    )
                  )}

                  <button
                    className="btn-nav"
                    disabled={page >= totalPages}
                    onClick={() => updateParams({ page: page + 1 })}
                    aria-label="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
